import React, { useState } from 'react';
import { Game, normalizeGamePricing } from '../../api/gameScore';
import { currencySymbol } from '../../utils/currency';

interface LivesPurchaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  game: Game;
}

export const LivesPurchaseModal: React.FC<LivesPurchaseModalProps> = ({
  isOpen,
  onClose,
  game,
}) => {
  // Normalize potential string values from backend (DECIMAL / INT via Sequelize often serialized as string)
  const withNormalized = normalizeGamePricing(game);
  const purchaseUnit = typeof withNormalized.livesPerUnit === 'number' ? withNormalized.livesPerUnit : 0;
  const normalizedLivesPrice = typeof withNormalized.livesPrice === 'number' ? withNormalized.livesPrice : parseFloat(String(withNormalized.livesPrice)) || 0;
  const symbol = currencySymbol(withNormalized.livesCurrency || 'AUD');
  // Compute minimum livesCount so that total cost is at least 1
  const minLivesCount = (() => {
    if (!purchaseUnit || !normalizedLivesPrice) return 1;
    const costPerLife = normalizedLivesPrice / purchaseUnit;
    return Math.ceil(1 / costPerLife);
  })();
  const [livesCount, setLivesCount] = useState<number>(minLivesCount); // Default to min allowed

  if (!isOpen) return null;

  const calculateCost = (lives: number): number => {
    if (!purchaseUnit || purchaseUnit <= 0) return 0;
    const costPerLife = normalizedLivesPrice / purchaseUnit;
    return Number((costPerLife * lives).toFixed(2));
  };

  // Redirect to payment page (standardized flow)
  const handlePurchase = () => {
    if (livesCount < 1) return;
    // Redirect to payment page with params (replace with your real payment URL/route)
    const params = new URLSearchParams({
      gameId: game.id,
      livesCount: String(livesCount)
    });
    window.location.href = `/customer/games/lives/checkout?${params.toString()}`;
  };

  const cost = calculateCost(livesCount);
  const priceDisplay = `${symbol}${cost} ${withNormalized.livesCurrency || 'AUD'}`;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Buy Extra Lives</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <h3 className="font-semibold text-lg mb-2">{game.displayName}</h3>
            <p className="text-gray-600 text-sm mb-4">
              Get extra plays for today! Lives expire at midnight.
            </p>
          </div>
          {/* Lives selector */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Extra Lives
            </label>
            <div className="flex items-center space-x-2 mb-3">
              <button
                type="button"
                onClick={() => setLivesCount(Math.max(minLivesCount, livesCount - 1))}
                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-bold"
                disabled={livesCount <= minLivesCount}
              >
                −
              </button>
              <input
                type="number"
                value={livesCount}
                onChange={(e) => {
                  const val = parseInt(e.target.value) || minLivesCount;
                  setLivesCount(Math.max(minLivesCount, val));
                }}
                className="w-20 text-center border border-gray-300 rounded px-3 py-1"
                min={minLivesCount}
                max="100"
              />
              <button
                type="button"
                onClick={() => setLivesCount(livesCount + 1)}
                className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-bold"
              >
                +
              </button>
            </div>
            {/* Quick select buttons */}
            <div className="flex space-x-2 mb-4">
              <button
                onClick={() => setLivesCount(purchaseUnit)}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
              >
                {symbol}{normalizedLivesPrice} ({purchaseUnit} lives)
              </button>
              <button
                onClick={() => setLivesCount(purchaseUnit * 2)}
                className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-sm hover:bg-blue-200"
              >
                {symbol}{normalizedLivesPrice * 2} ({purchaseUnit * 2} lives)
              </button>
            </div>
          </div>
          {/* Price display */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex justify-between items-center">
              <span className="font-medium">Total Cost:</span>
              <span className="text-2xl font-bold text-green-600">{priceDisplay}</span>
            </div>
            <div className="text-sm text-gray-600 mt-1">
              {purchaseUnit ? (
                <>
                  {livesCount} extra lives × {symbol}
                  {(normalizedLivesPrice / purchaseUnit).toFixed(4)} each
                </>
              ) : 'Pricing unavailable'}
            </div>
          </div>
          {/* Action buttons */}
          <div className="flex space-x-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handlePurchase}
              className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Pay {priceDisplay}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LivesPurchaseModal;